import './LessonCard.scss';

import { MessageBar, MessageBarBody } from '@fluentui/react-components';
import {
  ArrowRightRegular,
  ArrowUpRegular,
  PeopleTeam20Regular,
  Person20Regular,
} from '@fluentui/react-icons';
import { getSortedUserLabel, UserListSorting } from '@mnspro-cloud/services';
import { AbsenceState, LessonResponse } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { orderBy } from 'lodash';
import moment from 'moment-timezone';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../Button';
import { TagColorEnum } from '../../../Tags/Tags';
import { getCourseColor } from '../../../Utils';
import { toLessonCardClassbookEntry } from '../../Classbook.utils';
import {
  LessonSignOff,
  SignOffLevel,
  SignOffPendingUpdate,
} from '../../LessonSignOff/LessonSignOff.exports';
import { AbsenceStudentListEntry } from '../Absences/AbsencesStudentListEntry/AbsenceStudentListEntry';
import { EmptyLessonCardEntry } from '../EmptyLessonCardEntry/EmptyLessonCardEntry';
import { TeachingUnitEntry, TeachingUnitEntryType } from '../TeachingUnitEntry/TeachingUnitEntry';

export interface LessonCardProps {
  onCourseClick?: (lesson: LessonResponse) => void;
  lesson: LessonResponse;
  showClasses: boolean;
  showCourse: boolean;
  disabledSignOff?: boolean;
  selfAzureId?: string;
  onSignClick?: (level: SignOffLevel) => void;
  onUnsignClick?: (level: SignOffLevel) => void;
  pendingSignOffUpdates?: SignOffPendingUpdate[];
  /**
   * When a click handler is registered for the LessonCard and
   * showSelectLessonIcon is set to true, the component will display
   * an arrow icon that triggers the click handler instead when clicked
   * on the whole component.
   */
  showSelectLessonIcon?: boolean;
  preferenceSorting?: UserListSorting;
  /**
   * if the lesson is a multilessonchild we don't need to show anything
   * only a button to move to the parentmulti lesson
   */
  isMultiLessonChild?: boolean;
  isMultiLessonsSigning?: boolean;
}

/**
 * Represents a lesson unit
 */
export const LessonCard: React.FC<LessonCardProps> = (props) => {
  const { t } = useTranslation();

  const handleLessonClick = () => props.onCourseClick?.(props.lesson);

  // Sortiere die Absenzen basierend auf den Benutzereinstellungen
  const sortedAttendances = useMemo(
    () =>
      orderBy(
        props.lesson.attendances.filter(
          (a) => a.state === AbsenceState.absent || a.state === AbsenceState.absentExcused,
        ),
        [
          (absence) =>
            props.preferenceSorting === UserListSorting.LastName
              ? absence.affectedStudent.lastName?.toLowerCase() ?? ''
              : absence.affectedStudent.firstName?.toLowerCase() ?? '',
        ],
        ['asc'],
      ),
    [props.lesson.attendances, props.preferenceSorting],
  );

  return (
    <div
      id={props.isMultiLessonChild ? props.lesson.multiLessonId : props.lesson.id}
      className="lessonCard"
      style={{
        borderLeftColor: getCourseColor(props.lesson.course),
        scrollMarginTop: props.showCourse ? '60px' : '150px',
      }}
    >
      {props.showCourse && props.lesson.course.name && (
        <h3 className="title">
          <a
            role="link"
            onClick={handleLessonClick}
            onKeyDown={handleLessonClick}
            tabIndex={0}
            className={props.onCourseClick === undefined ? '' : 'clickable'}
          >
            {props.lesson.course.name}
          </a>
        </h3>
      )}
      <>
        <a
          role="link"
          onClick={props.showSelectLessonIcon ? undefined : handleLessonClick}
          onKeyDown={handleLessonClick}
          tabIndex={0}
        >
          {(props.showClasses || props.lesson.teacher) && (
            <div className="flex gap-2 classTeacherWrapper flex-1">
              {props.showClasses && (
                <div className="flex items-center">
                  {props.lesson.classes.map((currentClass) => (
                    <div key={currentClass.id} className="flex items-center gap-2 classEntry">
                      <PeopleTeam20Regular />
                      <span>{currentClass.name}</span>
                    </div>
                  ))}
                </div>
              )}
              {(props.lesson.teacher || props.showSelectLessonIcon) && (
                <div className="flex items-center gap-2 teacherEntry flex-1">
                  {props.lesson.teacher && (
                    <>
                      <Person20Regular />
                      <span>{props.lesson.teacher.displayName}</span>
                    </>
                  )}
                  {props.showSelectLessonIcon && (
                    <>
                      <div className="flex-1"></div>
                      <Button
                        icon={<ArrowRightRegular />}
                        appearance="transparent"
                        aria-label={t('classbook:lesson_aria_label', {
                          defaultValue: 'Wechsel zur angezeigten Stunde',
                        })}
                        onClick={handleLessonClick}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {!props.isMultiLessonChild && (
            <div className="teachingUnitEntriesWrapper">
              {sortedAttendances.map((absenceResponse, index) => (
                <AbsenceStudentListEntry
                  key={absenceResponse.id ?? index}
                  absenceResponse={absenceResponse}
                  preferenceSorting={props.preferenceSorting ?? UserListSorting.FirstName}
                />
              ))}
              {props.lesson.homework &&
                props.lesson.homework.map((homework) => (
                  <TeachingUnitEntry
                    key={homework.id}
                    type={TeachingUnitEntryType.Homework}
                    headline={homework.taskTitle ?? ''}
                    author={{
                      id: homework.createdBy?.id ?? '',
                      mailAddress: homework.createdBy?.mailAddress ?? '',
                      displayName: homework.createdBy?.displayName ?? '',
                      firstName: '',
                      lastName: '',
                    }}
                    subheadline={homework.taskDescription ?? ''}
                    tags={
                      homework.deadline
                        ? [
                            {
                              label: `${t('to_be_done_until', {
                                defaultValue: 'Zu bearbeiten bis',
                              })}: ${moment(homework.deadline).format('ll')}`,
                              color: TagColorEnum.Grey,
                            },
                          ]
                        : []
                    }
                  />
                ))}
              {props.lesson.lessonThemes &&
                props.lesson.lessonThemes.map((lessonTheme) => (
                  <TeachingUnitEntry
                    key={lessonTheme.id}
                    type={TeachingUnitEntryType.EducationalMaterial}
                    headline={lessonTheme.themeDescription ?? ''}
                    subheadline={lessonTheme.teacher?.displayName}
                  />
                ))}
              {props.lesson.classbookEntries &&
                props.lesson.classbookEntries.map(toLessonCardClassbookEntry).map((entry) => (
                  <TeachingUnitEntry
                    key={`classbook-entry-${entry.id}`}
                    author={entry.author}
                    headline={`${entry.definition.name}`}
                    subheadline={entry.comment}
                    tags={entry.affectedUsers.map((u) => ({
                      label: getSortedUserLabel(
                        u,
                        props.preferenceSorting ?? UserListSorting.FirstName,
                      ),
                      color: TagColorEnum.Grey,
                    }))}
                    type={TeachingUnitEntryType.ClassBookEntry}
                  />
                ))}
              {!props.lesson.attendances?.length &&
                !props.lesson.homework?.length &&
                !props.lesson.lessonThemes?.length &&
                !props.lesson.classbookEntries?.length && (
                  <div className="emptyAlertWrapper spacer">
                    <EmptyLessonCardEntry />
                  </div>
                )}
            </div>
          )}
        </a>

        {!props.disabledSignOff && !props.isMultiLessonChild && (
          <div className="flex justify-end items-center signedOfWrapper">
            <LessonSignOff
              schoolManagementSignOff={props.lesson.schoolHeadSignOffs}
              classSignOff={props.lesson.classSignOffs}
              gradeSignOff={props.lesson.gradeSignOffs}
              selfAzureId={props.selfAzureId}
              onSignClick={props.onSignClick}
              onUnsignClick={props.onUnsignClick}
              pendingSignOffUpdates={props.pendingSignOffUpdates}
              lessonEnd={new Date(props.lesson.end)}
              isMultiLessonsSigning={props.isMultiLessonsSigning}
            />
          </div>
        )}
      </>

      {props.isMultiLessonChild && (
        <div className="flex justify-end items-center multiLessonWrapper">
          <a className="multiLessonEntry" href={`#${props.lesson.multiLessonId}`}>
            <MessageBar intent="info" icon={<ArrowUpRegular />}>
              <MessageBarBody>
                {t('classbook:multilesson_alert', {
                  defaultValue:
                    'Diese Stunde ist eine Folgestunde. Wenn Sie diese Nachricht anklicken gelangen Sie zur vorangegangenen Stunde',
                })}
              </MessageBarBody>
            </MessageBar>
          </a>
        </div>
      )}
    </div>
  );
};
